import { yupResolver } from "@hookform/resolvers/yup";
import useIsMounted from "ismounted";
import { useContext, useEffect } from "react";
import { DeepPartial, useForm } from "react-hook-form";
import { combineLatest, Observable } from "rxjs";
import { map } from "rxjs/operators";
import { StepperContext } from "../../../../components/stepper/context";
import { Field } from "../../../../store/launch-configurations/models/internal/field";
import { useFieldSchema } from "./field-schema.hook";
import {
  HandleSubmitCallArgs,
  useHandleSubmitCallback,
} from "./handle-submit-callback.hook";

/**
 * This hook will take a list of fields and return form hook for them.
 * It takes care for outer form state and validation as well as persistance
 * throughout the steps.
 */
export function useStepForm({
  fields,
  defaultValues,
  persistInStorage,
  ...args
}: {
  fields: Array<Field.Field<string>>;
  defaultValues: DeepPartial<any>;
  persistInStorage: (x: any) => void;
} & HandleSubmitCallArgs) {
  const schema = useFieldSchema(fields);
  const isMounted = useIsMounted();

  const { back, setValidation } = useContext(StepperContext);

  const form = useForm<any>({
    resolver: yupResolver(schema),
    defaultValues,
  });

  const handleSubmitCallback = useHandleSubmitCallback({
    ...args,
    defaultValues,
  });

  /** Store the current state of the form to redux store */
  useEffect(() => {
    // Only call back because we're persisting next only if validation is valid. Otherwise we refresh the page and hide the errors.
    const sub = combineLatest([
      back,
      new Observable<any>((obs) => {
        const s = form.watch((d) => obs.next(d));
        return () => {
          s.unsubscribe();
        };
      }),
    ])
      .pipe(map(([_, vals]) => vals))
      .subscribe((vals) => {
        persistInStorage(vals);
      });

    return () => sub.unsubscribe();
  }, [back, form.watch, persistInStorage]);

  /** Set up the validation for the form */

  useEffect(() => {
    setValidation(() => () =>
      new Promise<void>((res, rej) => {
        form.handleSubmit(
          async (data) => {
            await handleSubmitCallback(data);
            persistInStorage(data);
            res();
          },
          (e) => {
            console.error(e);
            rej();
          }
        )();
      })
    );
    return () => {
      if (isMounted.current) {
        // We have to check if it's mounted otherwise it will clear current validation
        // This is because the prior page's clear validation is run after the new page's setValidation
        setValidation(() => () => Promise.resolve());
      }
    };
  }, [
    handleSubmitCallback,
    setValidation,
    form.handleSubmit,
    isMounted.current,
    persistInStorage,
  ]);

  return form;
}
